import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Search, Copy, Check, Loader2, X, History, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HISTORY_KEY = "admin_peta_code_history_v1";
const HISTORY_LIMIT = 20;

export default function AdminPetaCode() {
  const navigate = useNavigate();
  const adminToken = localStorage.getItem("admin_token");

  const [customerId, setCustomerId] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [customer, setCustomer] = useState(null); // { customer_id, nama, alamat, update_code_info }
  const [genLoading, setGenLoading] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(null); // { customer_id, nama, code, expires_at }
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin/login");
      return;
    }
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) setHistory(JSON.parse(raw) || []);
    } catch (_e) {
      setHistory([]);
    }
  }, [adminToken, navigate]);

  const pushHistory = (entry) => {
    setHistory((prev) => {
      const next = [entry, ...prev].slice(0, HISTORY_LIMIT);
      try { localStorage.setItem(HISTORY_KEY, JSON.stringify(next)); } catch (_e) { /* ignore */ }
      return next;
    });
  };

  const clearHistory = () => {
    if (!window.confirm("Hapus seluruh histori kode di perangkat ini?")) return;
    setHistory([]);
    try { localStorage.removeItem(HISTORY_KEY); } catch (_e) { /* ignore */ }
    toast.success("Histori kode dihapus.");
  };

  const lookupCustomer = useCallback(async (cidRaw) => {
    const cid = (cidRaw ?? customerId).trim();
    if (!cid) {
      toast.error("Masukkan ID pelanggan terlebih dahulu.");
      return null;
    }
    setLookupLoading(true);
    setCustomer(null);
    try {
      const res = await axios.get(
        `${API}/peta-global/customer/${encodeURIComponent(cid)}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const data = res.data || {};
      setCustomer(data);
      return data;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Do NOT auto-navigate on 401. Admin only logs out via "Keluar" button.
        toast.error("Gagal cek pelanggan (unauthorized).");
      } else if (status === 404) {
        toast.error(`Pelanggan ${cid} tidak ditemukan di Peta Global.`);
      } else {
        toast.error(err?.response?.data?.detail || "Gagal cek pelanggan.");
      }
      return null;
    } finally {
      setLookupLoading(false);
    }
  }, [adminToken, customerId, navigate]);

  const generateCode = async () => {
    const cid = customerId.trim();
    if (!cid) {
      toast.error("Masukkan ID pelanggan terlebih dahulu.");
      return;
    }
    setGenLoading(true);
    try {
      // Ensure customer exists; if lookup wasn't done, run it
      let cust = customer;
      if (!cust || cust.customer_id !== cid) {
        cust = await lookupCustomer(cid);
        if (!cust) { setGenLoading(false); return; }
      }
      const res = await axios.post(
        `${API}/admin/peta-global/${encodeURIComponent(cid)}/generate-update-code`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const code = res.data?.code;
      const expiresAt = res.data?.expires_at;
      const entry = {
        customer_id: cid,
        nama: cust?.nama || "-",
        code,
        expires_at: expiresAt,
        created_at: new Date().toISOString(),
      };
      setCopied(false);
      setGeneratedCode(entry);
      pushHistory(entry);
      toast.success(`Kode ${code} berhasil dibuat.`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Do NOT auto-navigate on 401. Admin only logs out via "Keluar" button.
        toast.error("Gagal membuat kode (unauthorized).");
      } else {
        toast.error(err?.response?.data?.detail || "Gagal membuat kode.");
      }
    } finally {
      setGenLoading(false);
    }
  };

  const copyCode = async (code) => {
    if (!code) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Kode disalin ke clipboard.");
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) {
      toast.error(`Gagal menyalin. Salin manual: ${code}`);
    }
  };

  const isExpired = (iso) => {
    if (!iso) return true;
    return new Date(iso).getTime() < Date.now();
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Kode Update Peta Global</h1>
            <p className="text-sm text-slate-500">
              Masukkan ID pelanggan untuk membuat kode (berlaku 15 menit) yang dipakai petugas membuka kunci edit pin & foto di Peta Global.
            </p>
          </div>
        </div>

        {/* Input + lookup card */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                ID Pelanggan
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value.trim().toUpperCase())}
                  placeholder="Contoh: 02030181"
                  className="flex-1 font-mono"
                  data-testid="petacode-customer-id-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      lookupCustomer();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => lookupCustomer()}
                  disabled={lookupLoading || !customerId.trim()}
                  data-testid="petacode-lookup-btn"
                >
                  {lookupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Cek Pelanggan
                </Button>
                <Button
                  type="button"
                  onClick={generateCode}
                  disabled={genLoading || !customerId.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="petacode-generate-btn"
                >
                  {genLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  Buat Kode
                </Button>
              </div>
            </div>

            {/* Customer info preview */}
            {customer && (
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/50" data-testid="petacode-customer-preview">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-cyan-600 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 dark:text-white">
                      {customer.nama || "-"} <span className="text-slate-500 font-normal">({customer.customer_id})</span>
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">{customer.alamat || "-"}</div>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs">
                      {customer.kecamatan && <span className="px-2 py-0.5 rounded bg-cyan-50 text-cyan-700">Kec: {customer.kecamatan}</span>}
                      {customer.rayon && <span className="px-2 py-0.5 rounded bg-cyan-50 text-cyan-700">R: {customer.rayon}</span>}
                      {customer.golongan && <span className="px-2 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700">{customer.golongan}</span>}
                      {customer.status && <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">{customer.status}</span>}
                      {(customer.latitude == null || customer.longitude == null) && (
                        <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700">Belum ada GPS</span>
                      )}
                    </div>
                    {customer.update_code_info?.code && !isExpired(customer.update_code_info?.expires_at) && (
                      <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        Pelanggan ini masih memiliki kode aktif: <b className="font-mono">{customer.update_code_info.code}</b>
                        {customer.update_code_info.expires_at && (
                          <> (sampai {new Date(customer.update_code_info.expires_at).toLocaleString("id-ID")})</>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Generated code display (persistent) */}
        {generatedCode && (
          <Card className="border-2 border-purple-300" data-testid="petacode-result-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  Pelanggan: <span className="font-medium text-slate-900 dark:text-white">{generatedCode.customer_id}</span>
                  <span className="text-slate-400"> — </span>
                  <span className="text-slate-700 dark:text-slate-200">{generatedCode.nama}</span>
                </div>
                <button
                  onClick={() => setGeneratedCode(null)}
                  className="text-slate-400 hover:text-slate-700"
                  data-testid="petacode-clear-result-btn"
                  aria-label="Tutup"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-3 bg-gradient-to-br from-purple-50 to-fuchsia-50 border-2 border-dashed border-purple-300 rounded-xl p-5 text-center">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Kode Berlaku 15 Menit</div>
                <div
                  className="text-4xl sm:text-5xl font-mono font-bold tracking-[0.3em] text-purple-700 select-all"
                  data-testid="petacode-result-value"
                >
                  {generatedCode.code}
                </div>
                {generatedCode.expires_at && (
                  <div className="text-xs text-slate-500 mt-3">
                    Berlaku sampai:{" "}
                    <span className="font-medium text-slate-700">
                      {new Date(generatedCode.expires_at).toLocaleString("id-ID")}
                    </span>
                  </div>
                )}
              </div>

              <Button
                onClick={() => copyCode(generatedCode.code)}
                className={`w-full mt-4 ${copied ? "bg-emerald-600 hover:bg-emerald-700" : "bg-purple-600 hover:bg-purple-700"} text-white`}
                data-testid="petacode-copy-btn"
              >
                {copied ? <><Check className="w-4 h-4 mr-2" /> Tersalin</> : <><Copy className="w-4 h-4 mr-2" /> Salin Kode</>}
              </Button>

              <p className="text-xs text-slate-500 text-center mt-3 leading-relaxed">
                Berikan kode ini kepada petugas. Kode hanya bisa digunakan <b>1x</b> untuk membuka kunci edit pin/foto pelanggan ini.
              </p>
            </CardContent>
          </Card>
        )}

        {/* History */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                <History className="w-4 h-4" />
                <h2 className="font-semibold">Histori Kode (Perangkat Ini)</h2>
                <span className="text-xs text-slate-400">{history.length} entri</span>
              </div>
              {history.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="text-xs text-red-600 hover:underline"
                  data-testid="petacode-clear-history-btn"
                >
                  Hapus Histori
                </button>
              )}
            </div>
            {history.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">Belum ada kode yang dibuat di perangkat ini.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {history.map((h, idx) => {
                  const expired = isExpired(h.expires_at);
                  return (
                    <div
                      key={`${h.code}-${idx}`}
                      className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${expired ? "border-slate-200 bg-slate-50 dark:bg-slate-800/30" : "border-purple-200 bg-purple-50/40 dark:bg-purple-900/10"}`}
                      data-testid={`petacode-history-row-${idx}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-lg tracking-widest text-purple-700">{h.code}</span>
                          {expired ? (
                            <span className="text-[10px] uppercase tracking-wide bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Kadaluarsa</span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Aktif</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 truncate">
                          {h.customer_id} — {h.nama}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          Dibuat: {new Date(h.created_at).toLocaleString("id-ID")}
                          {h.expires_at && <> · Berlaku sampai: {new Date(h.expires_at).toLocaleString("id-ID")}</>}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyCode(h.code)}
                        disabled={expired}
                        data-testid={`petacode-history-copy-${idx}`}
                      >
                        <Copy className="w-3.5 h-3.5 mr-1" /> Salin
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
